import debug from 'debug';
import { AuthType } from '../types/auth';
import { isActuallyOnline } from '../offline/checkConnectivity';

const log = debug('rois:auth:offline-session');

/**
 * Reapertura de la app SIN COBERTURA.
 *
 * ── El problema ───────────────────────────────────────────────────────────
 * La sesión vive en `sessionStorage`, que muere con la pestaña. Si a la tablet
 * se le cierra la app en un corredor sin cobertura, el operario no puede volver
 * a entrar: el login necesita red. Su trabajo no se pierde (sigue encolado),
 * pero se queda fuera y sin poder continuar la patrulla. En montaña ese es el
 * escenario normal, no el excepcional.
 *
 * ── Por qué NO se mueve el token y ya ─────────────────────────────────────
 * Porque el refresh token dura **60 días** (`auth.constants.ts`). Guardarlo tal
 * cual en un almacén duradero significaría que una tablet perdida da acceso
 * durante dos meses. Eso no es un intercambio razonable.
 *
 * Lo que se guarda aquí es una copia con **caducidad propia y corta**, del
 * tamaño de un turno. Pasado ese plazo se borra sola y hace falta login con red.
 * El resultado queda MÁS acotado que la situación actual: hoy una tablet con la
 * pestaña abierta ya mantiene sesión válida los 60 días completos, sin control
 * ninguno.
 *
 * ── Solo cuando el login es IMPOSIBLE ─────────────────────────────────────
 * La copia se usa **únicamente si no hay conectividad**. Con red, login normal
 * como siempre.
 *
 * Esto no es una cautela de más: las tablets se comparten entre turnos. Si se
 * restaurara siempre, el operario B abriría la tablet y entraría **como A** sin
 * enterarse — y las horas y actuaciones se registrarían a nombre equivocado.
 * Limitarlo a "sin cobertura" deja el caso normal (tablet en la base, con wifi)
 * exactamente como está hoy: cada uno entra con lo suyo.
 *
 * ── Se borra en CUALQUIER cierre de sesión ────────────────────────────────
 * No hay un punto único por el que pasen todas las invalidaciones de sesión —
 * hay tres, y dos se saltan el helper. Por eso `clearOfflineSession()` se llama
 * desde los tres, y el tripwire `offline-session-cleanup.spec.ts` falla si
 * aparece un cuarto camino sin limpiar.
 */

/** Clave en `localStorage` — duradera por definición, a diferencia de la sesión. */
export const OFFLINE_SESSION_KEY = 'rois_offline_session';

/**
 * Ventana de reapertura sin red. Un turno largo con margen.
 *
 * No tiene nada que ver con los 60 días del refresh token: es un límite NUESTRO
 * y más estricto. Al agotarse, la copia se borra y el operario tiene que entrar
 * con red como cualquier otro día.
 */
export const VENTANA_OFFLINE_MS = 12 * 60 * 60 * 1000;

interface CopiaSesion {
  auth: AuthType;
  guardadaEn: number;
  caducaEn: number;
}

/**
 * Guarda (o refresca) la copia para reapertura sin red.
 *
 * OJO: la ventana se cuenta desde el LOGIN, no desde la última actividad. Si se
 * renovara con cada uso, una tablet que nadie apaga tendría una sesión
 * indefinida — justo lo que se quiere evitar.
 */
export function saveOfflineSession(auth: AuthType | null): void {
  try {
    if (!auth?.accessToken || !auth?.user?.id) return;

    // Si ya hay copia vigente del MISMO usuario, se conserva su caducidad y solo
    // se actualizan los tokens (el access dura 1 h y se renueva por debajo).
    const previa = leerCopia();
    const mismaPersona = previa?.auth?.user?.id === auth.user.id;

    const copia: CopiaSesion = {
      auth,
      guardadaEn: Date.now(),
      caducaEn:
        mismaPersona && previa ? previa.caducaEn : Date.now() + VENTANA_OFFLINE_MS,
    };
    localStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify(copia));
    log(
      'copia guardada para %s · caduca en %d min',
      auth.user.id,
      Math.round((copia.caducaEn - Date.now()) / 60000)
    );
  } catch (error) {
    // Que esto falle no puede impedir entrar en la app.
    log('no se pudo guardar la copia: %o', error);
  }
}

function leerCopia(): CopiaSesion | null {
  try {
    const crudo = localStorage.getItem(OFFLINE_SESSION_KEY);
    if (!crudo) return null;
    const copia = JSON.parse(crudo) as CopiaSesion;
    if (!copia?.auth?.accessToken || typeof copia.caducaEn !== 'number') return null;
    return copia;
  } catch {
    return null;
  }
}

/**
 * Devuelve la copia si sigue vigente. Si caducó, la BORRA y devuelve `null`.
 *
 * La caducidad se comprueba al leer, no con un temporizador: un temporizador no
 * sobrevive a que la app esté cerrada, que es precisamente el caso.
 */
export function readOfflineSession(): AuthType | null {
  const copia = leerCopia();
  if (!copia) return null;

  if (Date.now() >= copia.caducaEn) {
    log('la copia CADUCÓ — se borra');
    clearOfflineSession();
    return null;
  }
  return copia.auth;
}

/** Borra la copia. Se llama desde TODO camino que cierre sesión. */
export function clearOfflineSession(): void {
  try {
    localStorage.removeItem(OFFLINE_SESSION_KEY);
    log('copia borrada');
  } catch {
    /* nada que hacer */
  }
}

/** Minutos que le quedan a la copia, o `null` si no hay o caducó. Diagnóstico. */
export function minutosRestantes(): number | null {
  const copia = leerCopia();
  if (!copia) return null;
  const restan = copia.caducaEn - Date.now();
  return restan > 0 ? Math.round(restan / 60000) : null;
}

/**
 * Devuelve la sesión a restaurar, o `null` si no procede restaurar nada.
 *
 * Este módulo NO escribe la sesión a propósito: hacerlo obligaría a importar
 * `sessionStorage.ts`, que a su vez importa de aquí para limpiar la copia en el
 * logout — un ciclo entre los dos módulos de sesión. Quien arranca la app hace
 * el escrito.
 *
 * ── Por qué NO basta `navigator.onLine` ───────────────────────────────────
 * La primera versión de esto usaba `navigator.onLine`, y estaba mal. Ese flag
 * solo dice si el sistema ve una interfaz de red levantada, no si se llega al
 * servidor. En el escenario real —tablet enganchada al router 4G del vehículo,
 * que ha perdido cobertura— vale `true` y la sesión NO se habría restaurado:
 * justo el caso que esto viene a resolver.
 *
 * Se descubrió probando con Chrome DevTools, cuya emulación de red corta el
 * tráfico pero deja `navigator.onLine` en `true`. Con Playwright no se veía,
 * porque su `setOffline` sí cambia el flag — el test pasaba y el producto
 * habría fallado en campo.
 *
 * La app ya tenía la respuesta: `isActuallyOnline()` hace peticiones reales con
 * timeout, y su propio comentario avisa de que «navigator.onLine is unreliable».
 *
 * El orden de las comprobaciones importa: la copia se mira ANTES del ping, para
 * no pagar una comprobación de red en el arranque cuando no hay nada que
 * restaurar (que es el caso corriente).
 */
export async function getSessionToRestoreOffline(): Promise<AuthType | null> {
  try {
    // Si esta pestaña ya tiene sesión, no hay nada que hacer.
    if (sessionStorage.getItem('user')) return null;

    // Barato primero: sin copia vigente no hay nada que decidir y nos ahorramos
    // el ping en todo arranque normal.
    const auth = readOfflineSession();
    if (!auth) {
      log('sin copia vigente — el operario tendrá que esperar a tener red');
      return null;
    }

    // 🔴 La condición que hace esto aceptable en una tablet compartida: solo
    // cuando entrar por las buenas es IMPOSIBLE. Con red, login normal.
    if (await isActuallyOnline()) {
      log('se llega al servidor — no se restaura nada, login normal');
      return null;
    }

    log('sin alcanzar el servidor y con copia vigente para %s', auth.user?.id);
    return auth;
  } catch (error) {
    log('no se pudo evaluar la sesión offline: %o', error);
    return null;
  }
}
